import { useState, useEffect, useCallback } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Shield, Activity, AlertTriangle, CheckCircle, Clock,
  TrendingUp, DollarSign, RefreshCw, FileText, Search,
  Loader2, ChevronRight, ArrowUpRight, ArrowDownRight,
  Eye, Lock, Database, Zap, BarChart3, ShieldCheck,
  ShieldAlert, CircleDot, Hash, Bell
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { BACKEND_URL } from '@/lib/backendUrl';
import TerminalFloorNav from '../components/TerminalFloorNav';

/* ── Health Score Ring ── */
function HealthRing({ score, status }) {
  const r = 44, c = 2 * Math.PI * r;
  const offset = c - (score / 100) * c;
  const color = score >= 80 ? '#22c55e' : score >= 60 ? '#f59e0b' : '#ef4444';
  return (
    <div className="relative w-32 h-32 shrink-0">
      <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
        <circle cx="50" cy="50" r={r} fill="none" stroke="#1a2235" strokeWidth="5" />
        <circle cx="50" cy="50" r={r} fill="none" stroke={color} strokeWidth="5"
          strokeDasharray={c} strokeDashoffset={offset} strokeLinecap="round"
          className="transition-all duration-1000" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-bold font-mono" style={{ color }}>{score}</span>
        <span className="text-[8px] text-[#64748b] uppercase tracking-widest">{status}</span>
      </div>
    </div>
  );
}

/* ── Pool Card ── */
function PoolCard({ name, balance, pct, denomination, icon: Icon, color }) {
  return (
    <div className="bg-[#0d1220] border border-[#1a2235] rounded-xl p-3">
      <div className="flex items-center gap-2 mb-2">
        <div className={cn("w-7 h-7 rounded-lg flex items-center justify-center", color)}>
          <Icon className="w-3.5 h-3.5" />
        </div>
        <div>
          <p className="text-[10px] text-[#64748b] uppercase tracking-wider">{name}</p>
          <p className="text-[8px] text-[#475569]">{pct}% of gross</p>
        </div>
      </div>
      <p className="text-lg font-bold font-mono">${balance.toLocaleString()}</p>
      <p className="text-[9px] text-[#475569]">{denomination}</p>
    </div>
  );
}

/* ── Issue Row ── */
function IssueRow({ issue }) {
  const colors = {
    critical: 'border-red-500 bg-red-500/5 text-red-400',
    high: 'border-orange-500 bg-orange-500/5 text-orange-400',
    medium: 'border-amber-500 bg-amber-500/5 text-amber-400',
    low: 'border-blue-500 bg-blue-500/5 text-blue-400',
  };
  const icons = {
    critical: ShieldAlert, high: AlertTriangle, medium: Clock, low: Eye,
  };
  const Icon = icons[issue.severity] || Eye;
  return (
    <div className={cn("border-l-2 rounded-r-lg px-3 py-2 flex items-start gap-2", colors[issue.severity])} data-testid={`argus-issue-${issue.id}`}>
      <Icon className="w-3.5 h-3.5 mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-[11px] leading-snug">{issue.message}</p>
        <div className="flex gap-2 mt-1">
          <Badge className="text-[7px] bg-white/5 border-white/10">{issue.type}</Badge>
          <Badge className={cn("text-[7px]", colors[issue.severity])}>{issue.severity}</Badge>
        </div>
      </div>
    </div>
  );
}

/* ── Audit Entry ── */
function AuditEntry({ entry }) {
  return (
    <div className="flex items-start gap-2 py-2 border-b border-[#1a2235]/60 last:border-0">
      <div className="w-6 h-6 rounded-md bg-[#1a2235] flex items-center justify-center shrink-0 mt-0.5">
        <Hash className="w-3 h-3 text-[#64748b]" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] leading-snug">{entry.summary}</p>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-[8px] text-[#475569] font-mono">{entry.timestamp?.slice(0, 16).replace('T', ' ')}</span>
          {entry.checksum && (
            <span className="text-[7px] text-[#374151] font-mono flex items-center gap-0.5">
              <Lock className="w-2 h-2" /> {entry.checksum}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ArgusDashboardPage() {
  const [dashboard, setDashboard] = useState(null);
  const [reconciliation, setReconciliation] = useState(null);
  const [anomalies, setAnomalies] = useState(null);
  const [report, setReport] = useState(null);
  const [alertConfig, setAlertConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reconLoading, setReconLoading] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [alertLoading, setAlertLoading] = useState(false);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/argus/dashboard`);
      const data = await res.json();
      setDashboard(data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  const fetchAlertConfig = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/argus/config`);
      const data = await res.json();
      setAlertConfig(data);
    } catch { /* ignore */ }
  }, []);

  const toggleAlerts = async () => {
    if (!alertConfig) return;
    setAlertLoading(true);
    try {
      const newEnabled = !alertConfig.enabled;
      const res = await fetch(`${BACKEND_URL}/api/argus/config?enabled=${newEnabled}&email=${encodeURIComponent(alertConfig.email)}&frequency=${alertConfig.frequency}`, { method: 'POST' });
      const data = await res.json();
      if (data.success) setAlertConfig(data.config);
    } catch { /* ignore */ }
    finally { setAlertLoading(false); }
  };

  const triggerManualAlert = async () => {
    setAlertLoading(true);
    try {
      await fetch(`${BACKEND_URL}/api/argus/trigger-alert`, { method: 'POST' });
      fetchDashboard();
    } catch { /* ignore */ }
    finally { setAlertLoading(false); }
  };

  const runReconciliation = async () => {
    setReconLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/argus/reconciliation`);
      const data = await res.json();
      setReconciliation(data);
      fetchDashboard();
    } catch { /* ignore */ }
    finally { setReconLoading(false); }
  };

  const fetchAnomalies = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/argus/anomalies`);
      const data = await res.json();
      setAnomalies(data);
    } catch { /* ignore */ }
  };

  const generateReport = async () => {
    setReportLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/argus/monthly-report`);
      const data = await res.json();
      setReport(data);
    } catch { /* ignore */ }
    finally { setReportLoading(false); }
  };

  useEffect(() => {
    fetchDashboard();
    fetchAnomalies();
    fetchAlertConfig();
  }, [fetchDashboard, fetchAlertConfig]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#080c16] flex items-center justify-center">
        <div className="flex items-center gap-3 text-[#64748b]">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span className="text-sm font-mono">Argus scanning...</span>
        </div>
      </div>
    );
  }

  if (!dashboard) return null;
  const { kpis, pools, monthly_trend, recent_audits, health_score, status } = dashboard;

  return (
    <div className="terminal-theme min-h-screen" data-testid="argus-dashboard">
      {/* Top Bar */}
      <div className="border-b border-[#1a2235] bg-[#0a0f1d]/80 backdrop-blur-md sticky top-0 z-10">
        <div className="max-w-[1800px] mx-auto px-4 py-2.5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="w-9 h-9 rounded-xl bg-purple-500/10 border border-purple-500/30 flex items-center justify-center">
                <Shield className="w-4.5 h-4.5 text-purple-400" />
              </div>
              <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-500 border-2 border-[#0a0f1d]" />
            </div>
            <div>
              <h1 className="text-sm font-bold tracking-tight" data-testid="argus-title">Argus AI</h1>
              <p className="text-[9px] text-[#475569]">Fee Ledger Reconciliation Agent</p>
            </div>
            <Badge className={cn("ml-2 text-[9px]",
              status === 'healthy' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' :
              status === 'warning' ? 'bg-amber-500/10 text-amber-400 border-amber-500/20' :
              'bg-red-500/10 text-red-400 border-red-500/20'
            )}>
              <CircleDot className="w-2.5 h-2.5 mr-1" /> {status}
            </Badge>
          </div>

          <div className="flex items-center gap-2">
            {/* Alert Config Toggle */}
            {alertConfig && (
              <div className="flex items-center gap-2 mr-2">
                <button onClick={toggleAlerts} disabled={alertLoading}
                  className={cn("flex items-center gap-1.5 h-7 px-3 rounded-md text-[10px] font-medium transition-all border",
                    alertConfig.enabled
                      ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20 hover:bg-emerald-500/20"
                      : "bg-[#1a2235] text-[#475569] border-[#1a2235] hover:text-[#94a3b8]"
                  )} data-testid="argus-alert-toggle">
                  <Bell className="w-3 h-3" />
                  {alertConfig.enabled ? 'Alerts ON' : 'Alerts OFF'}
                </button>
                <Button size="sm" variant="ghost" onClick={triggerManualAlert} disabled={alertLoading}
                  className="h-7 text-[10px] text-amber-400 hover:bg-amber-500/10 px-2" data-testid="argus-trigger-alert">
                  {alertLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />}
                </Button>
              </div>
            )}
            <Button size="sm" variant="ghost" onClick={runReconciliation} disabled={reconLoading}
              className="h-7 text-[10px] text-purple-400 hover:bg-purple-500/10 px-3" data-testid="argus-run-recon">
              {reconLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Search className="w-3 h-3 mr-1" />}
              {reconLoading ? 'Scanning...' : 'Run Reconciliation'}
            </Button>
            <Button size="sm" variant="ghost" onClick={generateReport} disabled={reportLoading}
              className="h-7 text-[10px] text-blue-400 hover:bg-blue-500/10 px-3" data-testid="argus-gen-report">
              {reportLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <FileText className="w-3 h-3 mr-1" />}
              {reportLoading ? 'Generating...' : 'Monthly Report'}
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1800px] mx-auto p-4 space-y-4">

        {/* Row 1: Health + KPIs */}
        <div className="flex gap-4 items-stretch">
          <div className="bg-[#0d1220] border border-[#1a2235] rounded-xl p-4 flex items-center justify-center" data-testid="argus-health-ring">
            <HealthRing score={health_score} status={status} />
          </div>
          <div className="flex-1 grid grid-cols-2 xl:grid-cols-4 gap-3">
            <div className="flex items-center gap-3 bg-[#0d1220] border border-[#1a2235] rounded-xl px-4 py-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center"><DollarSign className="w-5 h-5 text-emerald-400" /></div>
              <div><p className="text-2xl font-bold font-mono">${kpis.gross_revenue.toLocaleString()}</p><p className="text-[10px] text-[#64748b]">Gross Revenue</p></div>
            </div>
            <div className="flex items-center gap-3 bg-[#0d1220] border border-[#1a2235] rounded-xl px-4 py-3">
              <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center"><Zap className="w-5 h-5 text-purple-400" /></div>
              <div><p className="text-2xl font-bold font-mono">${kpis.total_fees.toLocaleString()}</p><p className="text-[10px] text-[#64748b]">Total Fees (6%)</p></div>
            </div>
            <div className="flex items-center gap-3 bg-[#0d1220] border border-[#1a2235] rounded-xl px-4 py-3">
              <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center"><Clock className="w-5 h-5 text-amber-400" /></div>
              <div><p className="text-2xl font-bold font-mono">{kpis.pending_payments}</p><p className="text-[10px] text-[#64748b]">Pending Payments</p></div>
            </div>
            <div className="flex items-center gap-3 bg-[#0d1220] border border-[#1a2235] rounded-xl px-4 py-3">
              <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center"><AlertTriangle className="w-5 h-5 text-red-400" /></div>
              <div><p className="text-2xl font-bold font-mono">{kpis.issues_detected}</p><p className="text-[10px] text-[#64748b]">Issues Detected</p></div>
            </div>
          </div>
        </div>

        {/* Row 2: Fee Pools */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="argus-pools">
          <PoolCard name="Operations" balance={pools.operations.balance} pct={pools.operations.pct} denomination="USD (Fiat)" icon={Database} color="bg-blue-500/10 text-blue-400" />
          <PoolCard name="Protocol Allocation" balance={pools.protocol_allocation.balance} pct={pools.protocol_allocation.pct} denomination="HERMES" icon={Zap} color="bg-purple-500/10 text-purple-400" />
          <PoolCard name="Treasury" balance={pools.treasury.balance} pct={pools.treasury.pct} denomination="HERMES/BTC" icon={Lock} color="bg-amber-500/10 text-amber-400" />
          <PoolCard name="getbitsy Revenue" balance={pools.getbitsy_revenue.balance} pct={pools.getbitsy_revenue.pct} denomination="HERMES" icon={TrendingUp} color="bg-emerald-500/10 text-emerald-400" />
        </div>

        {/* Row 3: Reconciliation + Anomalies + Audit */}
        <div className="grid grid-cols-12 gap-4">
          {/* Reconciliation Results */}
          <div className="col-span-12 lg:col-span-5 bg-[#0d1220] border border-[#1a2235] rounded-xl p-4" data-testid="argus-reconciliation">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-xs font-bold uppercase tracking-wider text-[#94a3b8] flex items-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5" /> Reconciliation
              </h2>
              {reconciliation && (
                <Badge className={cn("text-[9px]",
                  reconciliation.issues_found === 0 ? 'bg-emerald-500/10 text-emerald-400' : 'bg-amber-500/10 text-amber-400'
                )}>
                  {reconciliation.issues_found === 0 ? 'All Clear' : `${reconciliation.issues_found} issues`}
                </Badge>
              )}
            </div>
            {!reconciliation ? (
              <div className="text-center py-8">
                <Shield className="w-8 h-8 text-[#1a2235] mx-auto mb-2" />
                <p className="text-[11px] text-[#475569]">Click "Run Reconciliation" to scan</p>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex gap-3 mb-3">
                  <div className="flex-1 bg-[#080c16] rounded-lg p-2 text-center">
                    <p className="text-lg font-bold font-mono text-emerald-400">{reconciliation.bookings_clean}</p>
                    <p className="text-[8px] text-[#475569]">Clean</p>
                  </div>
                  <div className="flex-1 bg-[#080c16] rounded-lg p-2 text-center">
                    <p className="text-lg font-bold font-mono text-amber-400">{reconciliation.issues_found}</p>
                    <p className="text-[8px] text-[#475569]">Issues</p>
                  </div>
                  <div className="flex-1 bg-[#080c16] rounded-lg p-2 text-center">
                    <p className="text-lg font-bold font-mono text-blue-400">{reconciliation.bookings_checked}</p>
                    <p className="text-[8px] text-[#475569]">Checked</p>
                  </div>
                </div>
                <div className="space-y-1.5 max-h-[250px] overflow-y-auto">
                  {reconciliation.issues.map((issue) => <IssueRow key={issue.id} issue={issue} />)}
                  {reconciliation.issues.length === 0 && (
                    <div className="flex items-center gap-2 py-4 justify-center text-emerald-400/60">
                      <CheckCircle className="w-4 h-4" />
                      <span className="text-[11px]">All bookings reconciled successfully</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Anomalies */}
          <div className="col-span-12 lg:col-span-4 bg-[#0d1220] border border-[#1a2235] rounded-xl p-4" data-testid="argus-anomalies">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-xs font-bold uppercase tracking-wider text-[#94a3b8] flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5" /> Anomalies
              </h2>
              {anomalies && (
                <Badge className={cn("text-[9px]",
                  anomalies.total_anomalies === 0 ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'
                )}>
                  {anomalies.total_anomalies} detected
                </Badge>
              )}
            </div>
            {!anomalies ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-4 h-4 animate-spin text-[#475569]" />
              </div>
            ) : anomalies.anomalies.length === 0 ? (
              <div className="flex items-center gap-2 py-8 justify-center text-emerald-400/60">
                <CheckCircle className="w-4 h-4" />
                <span className="text-[11px]">No anomalies detected</span>
              </div>
            ) : (
              <div className="space-y-1.5 max-h-[300px] overflow-y-auto">
                {anomalies.anomalies.map((a) => <IssueRow key={a.id} issue={a} />)}
              </div>
            )}
          </div>

          {/* Audit Trail */}
          <div className="col-span-12 lg:col-span-3 bg-[#0d1220] border border-[#1a2235] rounded-xl p-4" data-testid="argus-audit-trail">
            <h2 className="text-xs font-bold uppercase tracking-wider text-[#94a3b8] flex items-center gap-1.5 mb-3">
              <Lock className="w-3.5 h-3.5" /> Audit Trail
            </h2>
            <div className="space-y-0.5 max-h-[300px] overflow-y-auto">
              {recent_audits.length === 0 ? (
                <p className="text-[11px] text-[#475569] text-center py-4">No audit entries yet</p>
              ) : (
                recent_audits.map((e, i) => <AuditEntry key={i} entry={e} />)
              )}
            </div>
          </div>
        </div>

        {/* Row 4: Monthly Report */}
        {report && (
          <div className="bg-gradient-to-r from-[#0d1220] via-[#111a30] to-[#0d1220] border border-blue-500/20 rounded-xl p-4" data-testid="argus-monthly-report">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 rounded-lg bg-blue-500/15 border border-blue-500/30 flex items-center justify-center">
                <FileText className="w-3.5 h-3.5 text-blue-400" />
              </div>
              <h2 className="text-xs font-bold uppercase tracking-wider text-blue-400">Monthly Report — {report.month}</h2>
              <Badge className="text-[7px] bg-blue-500/10 text-blue-300 border-blue-500/20">GPT-5.2</Badge>
            </div>
            <div className="prose prose-invert prose-xs max-w-none text-[12px] leading-relaxed text-[#cbd5e1] whitespace-pre-wrap" data-testid="argus-report-text">
              {report.report}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 pt-3 border-t border-[#1a2235]">
              <div className="text-center">
                <p className="text-lg font-bold font-mono text-blue-400">{report.data.bookings}</p>
                <p className="text-[8px] text-[#475569]">Bookings</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-bold font-mono text-emerald-400">${report.data.gross_revenue.toLocaleString()}</p>
                <p className="text-[8px] text-[#475569]">Revenue</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-bold font-mono text-purple-400">${report.data.fee_split.total_fee}</p>
                <p className="text-[8px] text-[#475569]">Fees Collected</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-bold font-mono text-amber-400">{report.data.pending_payments}</p>
                <p className="text-[8px] text-[#475569]">Pending</p>
              </div>
            </div>
          </div>
        )}

        {/* Row 5: Monthly Trend */}
        {monthly_trend.length > 0 && (
          <div className="bg-[#0d1220] border border-[#1a2235] rounded-xl p-4" data-testid="argus-trend">
            <h2 className="text-xs font-bold uppercase tracking-wider text-[#94a3b8] flex items-center gap-1.5 mb-3">
              <BarChart3 className="w-3.5 h-3.5" /> Monthly Trend
            </h2>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {monthly_trend.map((m) => (
                <div key={m.month} className="flex-shrink-0 min-w-[140px] bg-[#080c16] rounded-lg p-3">
                  <p className="text-[10px] font-bold text-[#94a3b8] mb-2">{m.month}</p>
                  <div className="space-y-1">
                    <div className="flex justify-between text-[9px]">
                      <span className="text-[#64748b]">Revenue</span>
                      <span className="font-mono text-emerald-400">${m.revenue.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-[9px]">
                      <span className="text-[#64748b]">Fees</span>
                      <span className="font-mono text-purple-400">${m.fees.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-[9px]">
                      <span className="text-[#64748b]">Bookings</span>
                      <span className="font-mono text-blue-400">{m.bookings}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <TerminalFloorNav />
    </div>
  );
}
